import React, { createContext, useState, useEffect, useContext, useCallback, ReactNode } from 'react';

// Define the sound types
export type SoundType = 
  | 'click' 
  | 'pop' 
  | 'win' 
  | 'lose' 
  | 'heal' 
  | 'play' 
  | 'sleep'
  | 'wrong'
  | 'type'
  | 'hover'
  | 'success'
  | 'error'
  | 'achievement';

interface SoundEffectMap {
  [key: string]: HTMLAudioElement;
}

// Context type definition
export interface SoundEffectsContextType {
  playSound: (sound: SoundType) => void;
  toggleSoundEffects: () => void;
  isSoundEnabled: boolean;
  toggleMusic: () => void;
  isMusicEnabled: boolean;
  currentMusic: string | null;
  playMusic: (music: string) => void;
  stopMusic: () => void;
}

// Create context
export const SoundEffectsContext = createContext<SoundEffectsContextType>({
  playSound: () => {},
  toggleSoundEffects: () => {},
  isSoundEnabled: true,
  toggleMusic: () => {},
  isMusicEnabled: true,
  currentMusic: null,
  playMusic: () => {},
  stopMusic: () => {},
});

// Custom hook to use the context
export const useSoundEffectsContext = () => useContext(SoundEffectsContext);

// Voice recording event system
export const voiceRecordingEvents = {
  START_RECORDING: 'START_RECORDING',
  STOP_RECORDING: 'STOP_RECORDING',
  CANCEL_RECORDING: 'CANCEL_RECORDING',
  RECORDING_COMPLETE: 'RECORDING_COMPLETE',
  started: 'RECORDING_STARTED',
  ended: 'RECORDING_ENDED'
};

export const dispatchVoiceRecordingEvent = (eventName: string, data?: any) => {
  const event = new CustomEvent(eventName, { detail: data });
  document.dispatchEvent(event);
};

interface SoundEffectsProviderProps {
  children: ReactNode;
}

export const SoundEffectsProvider: React.FC<SoundEffectsProviderProps> = ({ children }) => {
  // State for sound effects
  const [isSoundEnabled, setIsSoundEnabled] = useState<boolean>(
    localStorage.getItem('soundEffects') !== 'false'
  );
  
  // State for background music
  const [isMusicEnabled, setIsMusicEnabled] = useState<boolean>(
    localStorage.getItem('backgroundMusic') !== 'false'
  );
  
  const [currentMusic, setCurrentMusic] = useState<string | null>(null);
  const [soundEffects, setSoundEffects] = useState<SoundEffectMap>({});
  const [musicTracks, setMusicTracks] = useState<SoundEffectMap>({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize sound effects
  useEffect(() => {
    const sounds: SoundEffectMap = {
      click: new Audio('/sounds/type.wav'),
      pop: new Audio('/sounds/pop.mp3'),
      win: new Audio('/sounds/win.wav'),
      lose: new Audio('/sounds/lose.mp3'),
      wrong: new Audio('/sounds/wrong.wav'),
      heal: new Audio('/sounds/heal.wav'),
      play: new Audio('/sounds/play.ogg'),
      sleep: new Audio('/sounds/sleep.wav'),
      type: new Audio('/sounds/textloop.wav'),
      hover: new Audio('/sounds/pop.mp3'),
      success: new Audio('/sounds/win.wav'),
      error: new Audio('/sounds/wrong.wav'),
      achievement: new Audio('/sounds/win.wav')
    };
    
    // Set volume for each sound effect
    Object.values(sounds).forEach(sound => {
      sound.volume = 0.5;
    });
    
    setSoundEffects(sounds);
    
    // Initialize music tracks
    const music: SoundEffectMap = {
      'main': new Audio('/sounds/A World of Wonder.mp3'),
      'relaxed': new Audio('/sounds/A World of Wonder 2.mp3'),
      'active': new Audio('/sounds/Hearts Together.mp3'),
    };
    
    // Set volume and loop for music tracks
    Object.values(music).forEach(track => {
      track.volume = 0.3;
      track.loop = true;
    });
    
    setMusicTracks(music);
    setIsInitialized(true);
    
    return () => {
      // Clean up all audio elements
      Object.values(sounds).forEach(sound => {
        sound.pause();
        sound.currentTime = 0;
      });
      
      Object.values(music).forEach(track => {
        track.pause();
        track.currentTime = 0;
      });
    };
  }, []);

  // Toggle sound effects
  const toggleSoundEffects = useCallback(() => {
    setIsSoundEnabled(prevState => !prevState);
    localStorage.setItem('soundEffects', (!isSoundEnabled).toString());
  }, [isSoundEnabled]);

  // Toggle background music
  const toggleMusic = useCallback(() => {
    setIsMusicEnabled(prevState => !prevState);
    localStorage.setItem('backgroundMusic', (!isMusicEnabled).toString());
    
    // Stop any playing music if disabled
    if (isMusicEnabled && currentMusic && musicTracks[currentMusic]) {
      musicTracks[currentMusic].pause();
      musicTracks[currentMusic].currentTime = 0;
    } else if (!isMusicEnabled && currentMusic && musicTracks[currentMusic]) {
      musicTracks[currentMusic].play().catch(err => console.error("Error playing music:", err));
    }
  }, [isMusicEnabled, currentMusic, musicTracks]);

  // Play a sound effect
  const playSound = useCallback((sound: SoundType) => {
    if (!isSoundEnabled || !isInitialized || !soundEffects[sound]) return;
    
    try {
      // Reset sound to beginning if it's already playing
      soundEffects[sound].currentTime = 0;
      soundEffects[sound].play().catch(err => {
        console.error(`Error playing sound ${sound}:`, err);
      });
    } catch (error) {
      console.error(`Error playing sound ${sound}:`, error);
    }
  }, [isSoundEnabled, isInitialized, soundEffects]);

  // Play background music
  const playMusic = useCallback((music: string) => {
    if (!isMusicEnabled || !isInitialized || !musicTracks[music]) return;
    
    // Stop current music if playing
    if (currentMusic && musicTracks[currentMusic]) {
      musicTracks[currentMusic].pause();
      musicTracks[currentMusic].currentTime = 0;
    }
    
    // Play new music
    try {
      musicTracks[music].play().catch(err => {
        console.error(`Error playing music ${music}:`, err);
      });
      setCurrentMusic(music);
    } catch (error) {
      console.error(`Error playing music ${music}:`, error);
    }
  }, [isMusicEnabled, isInitialized, musicTracks, currentMusic]);

  // Stop background music
  const stopMusic = useCallback(() => {
    if (!currentMusic || !musicTracks[currentMusic]) return;
    
    musicTracks[currentMusic].pause();
    musicTracks[currentMusic].currentTime = 0;
    setCurrentMusic(null);
  }, [currentMusic, musicTracks]);

  // Value object for the context
  const value = {
    playSound,
    toggleSoundEffects,
    isSoundEnabled,
    toggleMusic,
    isMusicEnabled,
    currentMusic,
    playMusic,
    stopMusic,
  };

  return (
    <SoundEffectsContext.Provider value={value}>
      {children}
    </SoundEffectsContext.Provider>
  );
};

export default SoundEffectsProvider;
